import type { ActorInfo, ConnectionState } from './hubTypes.ts';
import { usePrefersLight } from '../features/groups/useMemberColour.ts';

interface ConnectionIndicatorProps {
  state: ConnectionState;
  present: ActorInfo[];
  currentUserId: string | undefined;
  /** Mutations applied locally but not yet confirmed by the server. */
  queued: number;
  isFlushing: boolean;
}

/**
 * One line telling the user where they stand: connection, unsent changes, and who else
 * has the list open.
 *
 * Deliberately a single control rather than three. Someone in a shop needs one glance to
 * answer "is what I'm looking at real, and did my taps land?" — separate indicators for
 * socket state and queue depth would make them assemble that answer themselves.
 *
 * The queue takes priority in the wording: "3 to send" is what actually matters to
 * someone, whereas the socket being momentarily down is not their problem.
 */
export function ConnectionIndicator({
  state,
  present,
  currentUserId,
  queued,
  isFlushing,
}: ConnectionIndicatorProps) {
  const prefersLight = usePrefersLight();
  const others = present.filter((actor) => actor.userId !== currentUserId);

  const { label, tone } = describe(state, queued, isFlushing);

  return (
    <div className="connection" role="status" aria-live="polite">
      <span className="connection__dot" data-state={tone} aria-hidden="true" />
      <span className="connection__label">{label}</span>

      {others.length > 0 && tone === 'live' && (
        <span className="connection__present">
          {' · '}
          {others.map((actor) => (
            <span
              key={actor.userId}
              className="connection__person"
              style={{ color: prefersLight ? actor.colourOnLight : actor.colourHex }}
            >
              {actor.displayName}
            </span>
          ))}
          {others.length === 1 ? ' is here' : ' are here'}
        </span>
      )}
    </div>
  );
}

type Tone = 'live' | 'syncing' | 'offline' | 'connecting';

function describe(
  state: ConnectionState,
  queued: number,
  isFlushing: boolean,
): { label: string; tone: Tone } {
  if (isFlushing) {
    return { label: 'Syncing…', tone: 'syncing' };
  }

  if (queued > 0) {
    // Say how many, because "1 change to send" is reassuring while "12" tells someone
    // they have been out of signal for a while and might want to check.
    return {
      label: `${queued} ${queued === 1 ? 'change' : 'changes'} to send`,
      tone: state === 'live' ? 'syncing' : 'offline',
    };
  }

  switch (state) {
    case 'live':
      return { label: 'Live', tone: 'live' };
    case 'syncing':
      return { label: 'Syncing…', tone: 'syncing' };
    case 'connecting':
      return { label: 'Connecting…', tone: 'connecting' };
    case 'offline':
      // Saved, not lost — that is the reassurance someone needs at this moment.
      return { label: 'Offline · changes are saved', tone: 'offline' };
  }
}
